import React, { useEffect, useState } from 'react'
import { Box, Button, Checkbox, Chip, CircularProgress, Grid, IconButton, InputAdornment, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { Close, Search, UploadFile, OpenInNew, CalendarMonth, Send } from '@mui/icons-material';
import styles from '../../component/addCategoryForm/addCategoryForm.module.css';
import NestedUserHeader from '../../component/reusable/NestedUserHeader';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../../utils/api';
import axios from 'axios';
import { toast } from 'react-toastify';
import ProductTable from '../../component/scheduleComps/ScheduleSearchTable';
import { getAllUsersThunk } from '../../redux/customer/customerSlice';
import { useDispatch, useSelector } from 'react-redux';
import { setschedulerefresh } from '../../redux/schedulecartslice';
import { useNavigate } from 'react-router-dom';
import DeliveryAddressPopup from '../../component/scheduleComps/DeliveryAddressPopup';

const FONT = `'Public Sans', sans-serif`;
const PRIMARY = '#512DA8';
const BORDER = '#E0E2E7';
const MUTED = '#667085';

const cardSx = {
    p: { xs: 2, md: 3 },
    borderRadius: '14px',
    border: `1px solid ${BORDER}`,
    boxShadow: '0 4px 14px rgba(16, 24, 40, 0.04)',
    fontFamily: FONT,
    mb: 2.5,
};

const sectionTitleSx = { fontFamily: FONT, fontWeight: 700, fontSize: '16px', color: '#1D1F2C' };
const sectionHintSx = { fontFamily: FONT, fontSize: '13px', color: MUTED, mt: 0.25 };

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        background: '#FAFAFC',
        fontFamily: FONT,
        fontSize: '14px',
        '& fieldset': { borderColor: '#D0D5DD' },
        '&:hover fieldset': { borderColor: '#B9B3D6' },
        '&.Mui-focused': { background: '#FFFFFF', boxShadow: '0 0 0 3px rgba(81, 45, 168, 0.12)' },
        '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: '1.5px' },
    },
    '& .MuiInputBase-input': { fontFamily: FONT, fontSize: '14px' },
};

const labelSx = { fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: '#344054', mb: 0.75, display: 'block' };

const primaryBtnSx = {
    textTransform: 'none',
    fontFamily: FONT,
    fontWeight: 600,
    borderRadius: '8px',
    background: PRIMARY,
    boxShadow: '0 2px 8px rgba(81, 45, 168, 0.3)',
    '&:hover': { background: '#45259A' },
};
const outlineBtnSx = {
    textTransform: 'none',
    fontFamily: FONT,
    fontWeight: 600,
    borderRadius: '8px',
    color: PRIMARY,
    borderColor: PRIMARY,
    '&:hover': { borderColor: PRIMARY, background: '#F0EEFF' },
};

const Field = ({ label, optional, error, children }) => (
    <Box>
        <Typography component="label" sx={labelSx}>
            {label} {optional && <span style={{ color: MUTED, fontWeight: 400 }}>(optional)</span>}
        </Typography>
        {children}
        {error && <Typography sx={{ color: '#D92D20', fontSize: '12px', mt: 0.5, fontFamily: FONT }}>{error}</Typography>}
    </Box>
);

const NewSchedule = () => {
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [search, setsearch] = useState('')
    const [searched, setSearched] = useState(false)
    const [selectedproducts, setselectedProducts] = useState([])
    const [quantityErrors, setQuantityErrors] = useState({});
    const [totalQuantityErrors, setTotalQuantityErrors] = useState({});
    const [editingProductId, setEditingProductId] = useState(null);
    const { allUsers } = useSelector((state) => state.customer);
    const [editingTotalProductId, setEditingTotalProductId] = useState(null);
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [open, setOpen] = useState(false);
    const [shippingAddress, selectShippingAddress] = useState(null)
    const [fileName, setFileName] = useState('')
    // Optional: send the PO to a vendor right after the schedule is created
    const [vendorId, setVendorId] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const formik = useFormik({
        initialValues: {
            purchaseOrderUri: '',
            poNumber: '',
            period: '',
            scheduleDate: '',
            remarks: '',
            userId: ''
        },
        validationSchema: Yup.object({
            // The PO file is optional: a PO number alone is enough
            purchaseOrderUri: Yup.string().url('Please upload a valid file'),
            poNumber: Yup.string().trim().max(60, 'PO number is too long'),
            // Optional: empty stays empty, a value must be a positive number
            period: Yup.number()
                .transform((value, originalValue) => (originalValue === '' || originalValue === null ? null : value))
                .nullable()
                .typeError('Period must be a number')
                .positive('Period must be a positive number'),
            scheduleDate: Yup.date()
                .typeError('Invalid date format')
                .required('Schedule Date is required'),
            remarks: Yup.string(),

            userId: Yup.string().required('Customer is required'),
        }),
        onSubmit: async (values) => {
            const address = allUsers?.find(i => i?._id === values.userId)
            selectShippingAddress(address)
            setOpen(true)
        },
    });

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        try {
            if (file) {
                setLoading(true);
                const body = {
                    key: `${Date.now()}_${file.name}`,
                    fileType: file.type,
                    fileName: file.name,
                };

                const { data, status } = await api.getPutSignedUrl(body);

                if (status === 200) {
                    await axios.put(data.data?.preSigned, file, {
                        headers: {
                            'Content-Type': file.type,
                        },
                    });
                    formik.setFieldValue('purchaseOrderUri', data?.data?.url);
                    formik.setFieldTouched('purchaseOrderUri', true, false);
                    setFileName(file.name);
                    toast.success('PO uploaded');
                }
            }
        } catch (err) {
            console.error(err);
            toast.error('Error uploading file');
        } finally {
            setLoading(false);
            // Lets the same file be chosen again after removing it
            e.target.value = '';
        }
    };

    const removeFile = () => {
        formik.setFieldValue('purchaseOrderUri', '');
        setFileName('');
    };

    const searchProduct = async (e) => {
        if (e) e.preventDefault()
        const term = search.trim()
        if (!term) {
            toast.info('Type a part number or description to search')
            return
        }
        try {
            setSearchLoading(true)
            // Part numbers can contain "#", "/" or "&", so the term must be encoded
            const { data } = await api.getExactSearch(`?search=${encodeURIComponent(term)}`)
            setProducts(Array.isArray(data?.data) ? data.data : [])
            setSearched(true)
        } catch (err) {
            console.log(err);
            toast.error(err?.response?.data?.message || 'Search failed, please try again')
        } finally {
            setSearchLoading(false)
        }
    }

    const clearSearch = () => {
        setsearch('');
        setProducts([]);
        setSearched(false);
    };

    // Results appear while typing (after 2 characters), without pressing Search
    useEffect(() => {
        const term = search.trim();
        if (term.length < 2) return undefined;
        const timer = setTimeout(async () => {
            try {
                setSearchLoading(true);
                const { data } = await api.getExactSearch(`?search=${encodeURIComponent(term)}`);
                setProducts(Array.isArray(data?.data) ? data.data : []);
                setSearched(true);
            } catch (err) {
                console.log(err);
            } finally {
                setSearchLoading(false);
            }
        }, 450);
        return () => clearTimeout(timer);
    }, [search]);

    const handleRemoveProduct = (productId) => {
        setselectedProducts(prev =>
            prev.filter(item => item.productId !== productId)
        );
        // Clear errors for removed product
        setQuantityErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[productId];
            return newErrors;
        });
        setTotalQuantityErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[productId];
            return newErrors;
        });
    }

    // Validate quantity against minimumMultiplier and total quantity
    const validateQuantity = (productId, value, totalQty) => {
        const product = selectedproducts.find(p => p.productId === productId);
        const minMultiplier = product?.productDetails?.inventory?.minimumMultiplier || 1;
        const numValue = Number(value);
        const numTotalQty = Number(totalQty);

        if (value === '') {
            setQuantityErrors(prev => ({ ...prev, [productId]: '' }));
            return false;
        }

        if (isNaN(numValue)) {
            setQuantityErrors(prev => ({ ...prev, [productId]: 'Please enter a valid number' }));
            return false;
        }

        if (numValue <= 0) {
            setQuantityErrors(prev => ({ ...prev, [productId]: 'Quantity must be greater than 0' }));
            return false;
        }

        if (numValue % minMultiplier !== 0) {
            setQuantityErrors(prev => ({
                ...prev,
                [productId]: `Quantity must be in multiples of ${minMultiplier}`
            }));
            return false;
        }

        if (numValue > numTotalQty) {
            setQuantityErrors(prev => ({
                ...prev,
                [productId]: `Cannot exceed total quantity (${totalQty})`
            }));
            return false;
        }

        setQuantityErrors(prev => ({ ...prev, [productId]: '' }));
        return true;
    };

    // Validate total quantity against minimumMultiplier
    const validateTotalQuantity = (productId, value) => {
        const product = selectedproducts.find(p => p.productId === productId);
        const minMultiplier = product?.productDetails?.inventory?.minimumMultiplier || 1;
        const numValue = Number(value);

        if (value === '') {
            setTotalQuantityErrors(prev => ({ ...prev, [productId]: '' }));
            return false;
        }

        if (isNaN(numValue)) {
            setTotalQuantityErrors(prev => ({ ...prev, [productId]: 'Please enter a valid number' }));
            return false;
        }

        if (numValue <= 0) {
            setTotalQuantityErrors(prev => ({ ...prev, [productId]: 'Quantity must be greater than 0' }));
            return false;
        }

        if (numValue % minMultiplier !== 0) {
            setTotalQuantityErrors(prev => ({
                ...prev,
                [productId]: `Quantity must be in multiples of ${minMultiplier}`
            }));
            return false;
        }

        setTotalQuantityErrors(prev => ({ ...prev, [productId]: '' }));
        return true;
    };

    const handleQuantityChange = (productId, newQuantity, totalQty) => {
        // Update the state immediately
        setselectedProducts(prev =>
            prev.map(item => {
                if (item.productId === productId) {
                    return {
                        ...item,
                        quantity: newQuantity === '' ? '' : parseInt(newQuantity, 10) || 0
                    };
                }
                return item;
            })
        );

        // Then validate
        validateQuantity(productId, newQuantity, totalQty);
    };

    const handleTotalQuantityChange = (productId, newQuantity, quantity) => {
        // Update the state immediately
        setselectedProducts(prev =>
            prev.map(item => {
                if (item.productId === productId) {
                    return {
                        ...item,
                        totalQty: newQuantity === '' ? '' : parseInt(newQuantity, 10) || 0,
                        // Ensure quantity doesn't exceed the new total quantity
                        quantity: Math.min(item.quantity, newQuantity === '' ? Infinity : parseInt(newQuantity, 10) || 0)
                    };
                }
                return item;
            })
        );

        // Then validate
        validateTotalQuantity(productId, newQuantity);

        // Also validate if existing quantity is still valid with new total
        const product = selectedproducts.find(p => p.productId === productId);
        if (product) {
            validateQuantity(productId, product.quantity, newQuantity);
        }
    };

    const handleQuantityBlur = (productId, isTotal = false) => {
        if (isTotal) {
            setEditingTotalProductId(null);
        } else {
            setEditingProductId(null);
        }

        const product = selectedproducts.find(p => p.productId === productId);
        if (product) {
            if (isTotal) {
                const isValid = validateTotalQuantity(productId, product.totalQty);
                if (!isValid) {
                    // Reset to last valid total quantity
                    setselectedProducts(prev =>
                        prev.map(p => {
                            if (p.productId === productId) {
                                return { ...p, totalQty: p.previousValidTotalQty || 1 };
                            }
                            return p;
                        })
                    );
                }
            } else {
                const isValid = validateQuantity(productId, product.quantity, product.totalQty);
                if (!isValid) {
                    // Reset to last valid quantity
                    setselectedProducts(prev =>
                        prev.map(p => {
                            if (p.productId === productId) {
                                return { ...p, quantity: p.previousValidQuantity || 1 };
                            }
                            return p;
                        })
                    );
                }
            }
        }
    };

    const handleQuantityFocus = (productId, isTotal = false) => {
        // Store the current valid quantity before editing
        setselectedProducts(prev =>
            prev.map(item => {
                if (item.productId === productId) {
                    return {
                        ...item,
                        previousValidQuantity: isTotal ? item.previousValidQuantity : item.quantity,
                        previousValidTotalQty: isTotal ? item.totalQty : item.previousValidTotalQty
                    };
                }
                return item;
            })
        );

        if (isTotal) {
            setEditingTotalProductId(productId);
        } else {
            setEditingProductId(productId);
        }
    };

    const isQuantityValid = (productId) => {
        const product = selectedproducts.find(p => p.productId === productId);
        if (!product) return false;

        const quantity = product.quantity;
        const totalQty = product.totalQty;
        const minMultiplier = product.productDetails?.inventory?.minimumMultiplier || 1;

        return (
            quantity > 0 &&
            totalQty > 0 &&
            quantity % minMultiplier === 0 &&
            totalQty % minMultiplier === 0 &&
            quantity <= totalQty
        );
    };

    const validateBeforeSubmit = () => {
        if (selectedproducts.length === 0) {
            toast.error('Please add at least one product: enter the quantity and click "+ Add to schedule" (or tick the box)');
            return false;
        }

        const hasInvalidQuantity = selectedproducts.some(item => !isQuantityValid(item.productId));
        if (hasInvalidQuantity) {
            toast.error('Please enter valid quantities (multiples of SPQ) for all items');
            return false;
        }

        return true;
    };

    const handleSubmit = () => {
        if (!validateBeforeSubmit()) {
            return;
        }
        // Proceed with form submission
        formik.handleSubmit();
    };

    const handleSaveSelection = async () => {
        if (submitting) return;
        const day = new Date(formik.values.scheduleDate).getDate();
        const formData = {
            ...formik.values,
            poNumber: formik.values.poNumber?.trim() || undefined,
            // Optional fields are left out when empty
            period: formik.values.period === '' ? undefined : Number(formik.values.period),
            remarks: formik.values.remarks?.trim() || undefined,
            products: selectedproducts.map(({ productId, quantity, totalQty }) => ({ productId, quantity, totalQty })),
            scheduleDate: day,
            shippingAddress: selectedAddress
        };

        setSubmitting(true);
        try {
            const res = await api.createScheduleenquiry(formData);
            const scheduleId = res?.data?.data?._id;
            dispatch(setschedulerefresh());
            toast.success(`Schedule${scheduleId ? ` #${scheduleId}` : ''} created`);

            // Then send the PO to the chosen vendor for the ticked products
            const toSend = selectedproducts.filter((p) => p.sendToVendor !== false).map((p) => p.productId);
            if (vendorId && scheduleId && toSend.length) {
                try {
                    const sent = await api.sendSchedulePoToVendor(scheduleId, { vendorId, productIds: toSend });
                    toast.success(sent?.data?.data?.message || 'PO sent to the vendor');
                } catch (err) {
                    toast.error(err?.response?.data?.message || 'Schedule created, but the PO could not be sent to the vendor');
                }
            }

            setOpen(false);
            navigate('/schedule/scheduleEnquires');
        } catch (err) {
            console.log(err);
            toast.error(err?.response?.data?.message || 'Could not create the schedule');
        } finally {
            setSubmitting(false);
        }
    };

    const toggleSendToVendor = (productId) => {
        setselectedProducts((prev) =>
            prev.map((p) => (p.productId === productId ? { ...p, sendToVendor: p.sendToVendor === false } : p))
        );
    };

    const handleAddtoSchedule = (productId, quantity, productDetails) => {
        if (!quantity || isNaN(quantity)) {
            toast.error('Please enter a valid quantity');
            return;
        }

        const numQuantity = Number(quantity);
        const minMultiplier = productDetails?.inventory?.minimumMultiplier || 1;

        if (numQuantity % minMultiplier !== 0) {
            toast.error(`Quantity must be in multiples of ${minMultiplier}`);
            return;
        }

        setselectedProducts(prev => {
            const existingIndex = prev.findIndex(item => item.productId === productId);

            if (existingIndex >= 0) {
                // Update existing product
                const updatedProducts = [...prev];
                updatedProducts[existingIndex] = {
                    ...updatedProducts[existingIndex],
                    quantity: numQuantity,
                    totalQty: numQuantity, // Set both to same value initially
                    productDetails
                };
                return updatedProducts;
            } else {
                // Add new product
                return [...prev, {
                    productId,
                    quantity: numQuantity,
                    totalQty: numQuantity,
                    productDetails,
                    previousValidQuantity: numQuantity,
                    previousValidTotalQty: numQuantity
                }];
            }
        });
        toast.success('Added to the schedule');
    };

    useEffect(() => {
        dispatch(getAllUsersThunk())
    }, [dispatch])

    const byCompany = (a, b) => (a?.companyName || a?.fullName || '').localeCompare(b?.companyName || b?.fullName || '');
    // Customers and vendors come from the same user list
    const customerOptions = (allUsers || []).filter((u) => u?.userType !== 'VENDOR').sort(byCompany);
    const vendorOptions = (allUsers || []).filter((u) => u?.userType === 'VENDOR').sort(byCompany);
    const sendCount = selectedproducts.filter((p) => p.sendToVendor !== false).length;

    const hasInvalidRows = selectedproducts.some(item => !isQuantityValid(item.productId));

    // Unit price from the product's price breaks for the scheduled quantity
    // (same rule the backend uses); 0 when the part has no price yet.
    const unitPriceFor = (item) => {
        const table = Array.isArray(item?.productDetails?.quantityTable) ? item.productDetails.quantityTable : [];
        if (!table.length) return 0;
        const qty = Number(item?.quantity) || 0;
        const sorted = [...table].sort((a, b) => Number(a?.breakQty) - Number(b?.breakQty));
        const row = sorted.find((entry) => qty <= Number(entry?.breakQty)) || sorted[sorted.length - 1];
        return Number(row?.unitPrice) || 0;
    };
    const money = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;
    const scheduleTotal = selectedproducts.reduce((sum, item) => sum + unitPriceFor(item) * (Number(item.quantity) || 0), 0);
    const unpricedCount = selectedproducts.filter((item) => unitPriceFor(item) === 0).length;

    return (
        <>
            <NestedUserHeader
                index="New schedule"
                root="Dashboard"
                buttonText={'Add Subcategory'}
                actions={
                    <>
                        <input
                            type="file"
                            style={{ display: 'none' }}
                            id="fileUpload"
                            accept=".xlsx, .xls, .csv, .pdf"
                            onChange={handleFileUpload}
                        />
                        {formik.values.purchaseOrderUri ? (
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ height: 40, px: 1.5, borderRadius: '8px', border: '1px solid #ABEFC6', background: '#ECFDF3', maxWidth: 360 }}>
                                <UploadFile sx={{ fontSize: 18, color: '#067647' }} />
                                <Typography
                                    component="a"
                                    href={formik.values.purchaseOrderUri}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={fileName || formik.values.purchaseOrderUri}
                                    sx={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: '#067647', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 0.5 }}
                                >
                                    PO: {fileName || decodeURIComponent(formik.values.purchaseOrderUri.split('/').pop() || 'Uploaded file')}
                                    <OpenInNew sx={{ fontSize: 13 }} />
                                </Typography>
                                <IconButton size="small" onClick={removeFile} aria-label="remove file">
                                    <Close sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Stack>
                        ) : (
                            <Button
                                component="label"
                                htmlFor="fileUpload"
                                variant="contained"
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <UploadFile />}
                                sx={{ ...primaryBtnSx, height: 40, px: 2.25, whiteSpace: 'nowrap' }}
                            >
                                {loading ? 'Uploading…' : 'Upload PO'}
                            </Button>
                        )}
                    </>
                }
            />

            <Box sx={{ px: { xs: 1.5, md: 2.5 }, pb: 6 }}>
                {/* Step 1: products */}
                <Paper sx={cardSx}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 2 }}>
                        <Box>
                            <Typography sx={sectionTitleSx}>1. Add products</Typography>
                            <Typography sx={sectionHintSx}>Search by part number or description, set the quantity and add it to the schedule.</Typography>
                        </Box>
                        {selectedproducts.length > 0 && (
                            <Chip label={`${selectedproducts.length} product(s) selected`} sx={{ background: '#F0EEFF', color: PRIMARY, fontWeight: 600, fontFamily: FONT }} />
                        )}
                    </Stack>

                    <Box component="form" onSubmit={searchProduct} sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            placeholder="Search part number, e.g. 0805B104K500CT"
                            value={search}
                            onChange={(e) => setsearch(e.target.value)}
                            sx={{ ...fieldSx, flex: '1 1 320px', maxWidth: 560 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: MUTED, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: search ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={clearSearch} aria-label="clear search">
                                            <Close sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={searchLoading}
                            startIcon={searchLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Search />}
                            sx={{ ...primaryBtnSx, px: 2.5, height: 40 }}
                        >
                            {searchLoading ? 'Searching…' : 'Search'}
                        </Button>
                    </Box>

                    {products?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{ ...sectionHintSx, mb: 1 }}>
                                {products.length} result(s){products.length >= 100 ? ' (showing the first 100, refine the search for more)' : ''}
                            </Typography>
                            <ProductTable
                                products={products}
                                handleAddtoSchedule={handleAddtoSchedule}
                                handleRemoveFromSchedule={handleRemoveProduct}
                                selectedIds={selectedproducts.map((p) => p.productId)}
                            />
                        </Box>
                    )}
                    {searched && !searchLoading && products?.length === 0 && (
                        <Box sx={{ mt: 2, p: 2, borderRadius: '10px', background: '#FFFAEB', border: '1px solid #FEDF89' }}>
                            <Typography sx={{ fontFamily: FONT, fontSize: '13.5px', color: '#B54708' }}>
                                No products found for "{search.trim()}". Try a part number, manufacturer part number or a word from the description.
                            </Typography>
                        </Box>
                    )}

                    {/* Selected Products List */}
                    {selectedproducts.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography sx={{ ...sectionTitleSx, fontSize: '15px', mb: 1 }}>Selected products</Typography>
                            <Box
                                sx={{
                                    overflowX: 'auto',
                                    borderRadius: '10px',
                                    border: `1px solid ${BORDER}`,
                                    '& table': { tableLayout: 'fixed', borderCollapse: 'collapse' },
                                    '& th': { fontFamily: FONT, fontSize: '12px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', background: '#F9FAFB', padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: `1px solid ${BORDER}` },
                                    '& td': { fontFamily: FONT, fontSize: '14px', color: '#1D1F2C', padding: '12px 14px', verticalAlign: 'middle', borderBottom: `1px solid ${BORDER}` },
                                    '& tbody tr:last-of-type td': { borderBottom: 'none' },
                                    '& th:nth-of-type(2), & td:nth-of-type(2)': { textAlign: 'center' },
                                    '& th:nth-of-type(5), & td:nth-of-type(5), & th:nth-of-type(6), & td:nth-of-type(6)': { textAlign: 'right' },
                                    '& th:last-of-type, & td:last-of-type': { textAlign: 'right' },
                                    '& input[type="number"]': { width: '100%', maxWidth: 120, height: 36, padding: '0 10px', borderRadius: '8px', border: '1px solid #D0D5DD', background: '#FAFAFC', fontFamily: FONT, fontSize: '14px', boxSizing: 'border-box', margin: 0 },
                                    '& input[type="number"]:focus': { outline: 'none', borderColor: PRIMARY, background: '#fff', boxShadow: '0 0 0 3px rgba(81, 45, 168, 0.12)' },
                                }}
                            >
                                <table style={{ margin: 0, width: '100%' }}>
                                    <colgroup>
                                        <col style={{ width: '26%' }} />
                                        <col style={{ width: '8%' }} />
                                        <col style={{ width: '14%' }} />
                                        <col style={{ width: '14%' }} />
                                        <col style={{ width: '12%' }} />
                                        <col style={{ width: '12%' }} />
                                        {vendorId && <col style={{ width: '10%' }} />}
                                        <col style={{ width: vendorId ? '8%' : '14%' }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>SPQ</th>
                                            <th>Total qty</th>
                                            <th>Schedule qty</th>
                                            <th>Unit price</th>
                                            <th>Line total</th>
                                            {vendorId && <th style={{ textAlign: 'center' }}>Send to vendor</th>}
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedproducts.map((item, index) => {
                                            const minMultiplier = item?.productDetails?.inventory?.minimumMultiplier || 1;
                                            return (
                                                <tr key={item.productId || index}>
                                                    <td>
                                                        <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{item.productDetails?.productOverview?.productNumber}</div>
                                                        <div style={{ fontSize: 12, color: MUTED }}>
                                                            {item.productDetails?.productOverview?.manufacturer?.[0]?.name || ''}
                                                        </div>
                                                    </td>
                                                    <td>{minMultiplier}</td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={item.totalQty}
                                                            onChange={(e) => handleTotalQuantityChange(item.productId, e.target.value, item.quantity)}
                                                            onBlur={() => handleQuantityBlur(item.productId, true)}
                                                            onFocus={() => handleQuantityFocus(item.productId, true)}
                                                            min="1"
                                                            step={minMultiplier}
                                                            className={styles.quantityInput}
                                                        />
                                                        {totalQuantityErrors[item.productId] && (
                                                            <div className={styles.errorText}>
                                                                {totalQuantityErrors[item.productId]}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => handleQuantityChange(item.productId, e.target.value, item.totalQty)}
                                                            onBlur={() => handleQuantityBlur(item.productId)}
                                                            onFocus={() => handleQuantityFocus(item.productId)}
                                                            min="1"
                                                            max={item.totalQty}
                                                            step={minMultiplier}
                                                            className={styles.quantityInput}
                                                        />
                                                        {quantityErrors[item.productId] && (
                                                            <div className={styles.errorText}>
                                                                {quantityErrors[item.productId]}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        {unitPriceFor(item) > 0 ? (
                                                            <span style={{ fontWeight: 600 }}>{money(unitPriceFor(item))}</span>
                                                        ) : (
                                                            <Chip size="small" label="No price" sx={{ height: 22, fontSize: '11px', fontWeight: 600, background: '#FFFAEB', color: '#B54708', border: '1px solid #FEDF89' }} />
                                                        )}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                                                        {unitPriceFor(item) > 0 ? money(unitPriceFor(item) * (Number(item.quantity) || 0)) : <span style={{ color: MUTED }}>—</span>}
                                                    </td>
                                                    {vendorId && (
                                                        <td style={{ textAlign: 'center' }}>
                                                            <Checkbox
                                                                size="small"
                                                                checked={item.sendToVendor !== false}
                                                                onChange={() => toggleSendToVendor(item.productId)}
                                                                sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }}
                                                            />
                                                        </td>
                                                    )}
                                                    <td>
                                                        <Button size="small" color="error" onClick={() => handleRemoveProduct(item.productId)} sx={{ textTransform: 'none', fontFamily: FONT }}>
                                                            Remove
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </Box>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5} sx={{ mt: 1.5 }}>
                                <Typography sx={{ fontFamily: FONT, fontSize: '12.5px', color: unpricedCount > 0 ? '#B54708' : MUTED }}>
                                    {unpricedCount > 0
                                        ? `${unpricedCount} product(s) have no price yet, so they are not in the total. Add a price or request a quote.`
                                        : `${selectedproducts.length} product(s) · prices from the catalog price breaks`}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, px: 2, py: 1.25, borderRadius: '10px', background: '#F0EEFF', border: '1px solid #E0D7FA' }}>
                                    <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#5B4B8A', whiteSpace: 'nowrap' }}>Schedule total (excl. GST)</Typography>
                                    <Typography sx={{ fontFamily: FONT, fontSize: '20px', fontWeight: 700, color: PRIMARY, whiteSpace: 'nowrap' }}>{money(scheduleTotal)}</Typography>
                                </Box>
                            </Stack>
                        </Box>
                    )}
                </Paper>

                {/* Step 2: details */}
                <Paper sx={cardSx}>
                    <Box sx={{ mb: 2.5 }}>
                        <Typography sx={sectionTitleSx}>2. Schedule details</Typography>
                        <Typography sx={sectionHintSx}>Pick the customer, add the PO and choose the monthly schedule date.</Typography>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Field label="Customer (company)" error={formik.touched.userId && formik.errors.userId}>
                                <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    name="userId"
                                    value={formik.values.userId}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    sx={fieldSx}
                                    SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
                                >
                                    <MenuItem value="" sx={{ fontFamily: FONT, color: MUTED }}>Select customer</MenuItem>
                                    {customerOptions.map((i) => (
                                        <MenuItem key={i?._id} value={i?._id} sx={{ fontFamily: FONT }}>
                                            <Box>
                                                <Typography sx={{ fontFamily: FONT, fontSize: '14px', fontWeight: 600 }}>
                                                    {i?.companyName || i?.fullName || i?.email}
                                                </Typography>
                                                <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: MUTED }}>
                                                    {i?.companyName ? `${i?.fullName || ''}${i?.email ? ` · ${i.email}` : ''}` : 'No company name'}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Field>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Field label="PO Number" error={formik.touched.poNumber && formik.errors.poNumber}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    name="poNumber"
                                    placeholder="e.g. PO-2026-0142"
                                    value={formik.values.poNumber}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    sx={fieldSx}
                                />
                            </Field>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Field label="Schedule Date" error={formik.touched.scheduleDate && formik.errors.scheduleDate}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="date"
                                    name="scheduleDate"
                                    value={formik.values.scheduleDate}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    sx={fieldSx}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonth sx={{ color: MUTED, fontSize: 18 }} /></InputAdornment> }}
                                />
                                <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: MUTED, mt: 0.5 }}>Deliveries repeat on this day of every month.</Typography>
                            </Field>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Field label="Months Period" optional error={formik.touched.period && formik.errors.period}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="number"
                                    name="period"
                                    placeholder="e.g. 6"
                                    value={formik.values.period}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    sx={fieldSx}
                                    inputProps={{ min: 1 }}
                                />
                            </Field>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Field label="Project remarks" optional error={formik.touched.remarks && formik.errors.remarks}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    name="remarks"
                                    placeholder="Anything the team should know"
                                    value={formik.values.remarks}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    sx={fieldSx}
                                />
                            </Field>
                        </Grid>
                    </Grid>

                    {/* Send PO to vendor (optional) */}
                    <Box sx={{ mt: 3, p: 2, borderRadius: '12px', border: `1px solid ${BORDER}`, background: '#FAFAFC' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
                            <Box>
                                <Typography sx={{ ...sectionTitleSx, fontSize: '15px', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Send sx={{ fontSize: 18, color: PRIMARY }} /> Send PO to vendor <span style={{ color: MUTED, fontWeight: 400, fontSize: 13 }}>(optional)</span>
                                </Typography>
                                <Typography sx={sectionHintSx}>
                                    {vendorId
                                        ? `The PO goes to this vendor for ${sendCount} ticked product(s) when you submit. The vendor gets an email and a notification.`
                                        : 'Choose a vendor to send this PO to them after the schedule is created.'}
                                </Typography>
                            </Box>
                            <TextField
                                select
                                size="small"
                                value={vendorId}
                                onChange={(e) => setVendorId(e.target.value)}
                                sx={{ ...fieldSx, minWidth: { md: 320 } }}
                                SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
                            >
                                <MenuItem value="" sx={{ fontFamily: FONT, color: MUTED }}>Don't send to a vendor</MenuItem>
                                {vendorOptions.map((v) => (
                                    <MenuItem key={v?._id} value={v?._id} sx={{ fontFamily: FONT }}>
                                        <Box>
                                            <Typography sx={{ fontFamily: FONT, fontSize: '14px', fontWeight: 600 }}>{v?.companyName || v?.fullName || v?.email}</Typography>
                                            <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: MUTED }}>
                                                {[v?.companyName ? v?.fullName : '', v?.email].filter(Boolean).join(' · ')}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                        {vendorId && sendCount === 0 && selectedproducts.length > 0 && (
                            <Typography sx={{ color: '#B54708', fontSize: '12.5px', mt: 1, fontFamily: FONT }}>
                                No product is ticked under "Send to vendor", so nothing will be sent.
                            </Typography>
                        )}
                    </Box>

                    <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${BORDER}` }}>
                        <Button variant="outlined" onClick={() => navigate('/schedule/scheduleEnquires')} sx={{ ...outlineBtnSx, px: 2.5 }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={hasInvalidRows || submitting}
                            startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : vendorId ? <Send /> : null}
                            sx={{ ...primaryBtnSx, px: 3 }}
                        >
                            {submitting ? 'Submitting…' : vendorId ? 'Approve, submit & send PO' : 'Approve & submit'}
                        </Button>
                    </Stack>
                </Paper>
            </Box>

            <DeliveryAddressPopup
                selectedAddress={selectedAddress}
                setSelectedAddress={setSelectedAddress}
                setOpen={setOpen}
                open={open}
                handleSubmit={handleSaveSelection}
                customer={shippingAddress}
            />
        </>
    )
}

export default NewSchedule
